import { v1 as uuid } from 'uuid';
import { stats } from '@fiverr-private/obs';
import { ACTIVITY_ROUTE, ERRORS, METRIC } from './constants';

let baseURL, appName, metricPrefix;

const hostname = location.hostname;

export const init = (url, app) => {
    baseURL = url;
    appName = app;
    metricPrefix = `services.${appName}.bianka`;
};

/**
 * @typedef {Object} user
 * @property {number} id
 */

/**
 * @typedef {Object} locale
 * @property {string} id
 */

/**
 * Reports an activity event to Bianka.
 *
 * @function reportActivity
 * @param {Object} options.event - The client event (according the defined proto schema) - required
 * @param {string} options.type - The client event type (packageName.eventName) - required
 * @param {string} options.uid - Device's uid - required
 * @param {string} options.contextId - The context id of the request - required
 * @param {string} options.platform - The platform the event was produced from (web, mobile_web, app) - required
 * @param {string} options.pageName - The name of the page that the activity comes from - required
 * @param {user} options.user - The user that performed the action - optional
 * @param {locale} options.locale - The current session locale - optional
 * @param {boolean} [options.testing=true] - When `true` send to raw_proto.events, otherwise send to raw.events - optional
 */
export const reportActivity = async(options) => {
    try {
        const request = formatActivityRequest(options);
        const start = Date.now();
        const headers = formatHeaders();
        const browserSupportsKeepalive = 'keepalive' in new Request('');

        const response = await fetch(combineURLs(baseURL, ACTIVITY_ROUTE), {
            method: 'POST',
            body: JSON.stringify(request),
            headers,
            keepalive: browserSupportsKeepalive
        });

        if (!response.ok) {
            stats.time(metricPrefix, `${METRIC.ACTIVITY}.failure.${response.status}`, Date.now() - start);

            return await formatServerError(response);
        }

        stats.time(metricPrefix, `${METRIC.ACTIVITY}.success`, Date.now() - start);

        return { status: response.status };
    } catch (error) {
        stats.count(metricPrefix, `${METRIC.ACTIVITY}.failure.0`);

        return formatClientError(error);
    }
};

/**
 * @typedef {Object} ActivityEvent
 * @property {metadata} metadata
 * @property {string} uid - Device's uid
 * @property {string} platform - The platform the event was produced from (web, mobile_web, app)
 * @property {pageView} pageView
 * @property {pageViewV2} pageViewV2
 * @property {click} click
 * @property {hover} hover
 * @property {impression} impression
 */

/**
 * Format Bianka's activity request.
 *
 * @function formatActivityRequest
 * @param {object} options.event - The client event
 * @param {string} options.type - The client event type (packageName.eventName)
 * @param {string} options.uid - Device's uid
 * @param {string} options.contextId - The context id of the request
 * @param {user} options.user - The user that performed the action
 * @param {locale} options.locale - The current session locale
 * @param {boolean} options.testing - When `true` send to raw_proto.events, otherwise send to raw.events
 * @param {string} options.platform - The platform the event was produced from (web, mobile_web, app)
 * @return {ActivityEvent}
 */
const formatActivityRequest = ({ event, type, uid, contextId, pageName, user, locale, testing = true, platform }) => {
    event['@type'] = `type.googleapis.com/${type}`;

    const activityEvent = {
        metadata: formatMetadata({ testing, contextId }),
        page: formatPage({ pageName }),
        uid,
        platform: formatPlatform({ platform }),
        event
    };

    if (user) {
        activityEvent.user = user;
    }

    if (locale) {
        activityEvent.locale = locale;
    }

    return activityEvent;
};

/**
 * Format Bianka request's metadata.
 *
 * @function formatMetadata
 * @param {boolean} options.testing - When `true` send to raw_proto.events, otherwise send to raw.events
 * @param {string} options.contextId - The context id of the request
 * @return {metadata}
 */
const formatMetadata = ({ testing, contextId }) => ({
    id: uuid(),
    context_id: contextId,
    prod: !testing,
    created_at: Date.now(),
    hostname
});

/**
 * Format Bianka request's platform.
 *
 * @function formatPlatform
 * @param {string} options.platform - The platform the event was produced from (web, mobile_web, app)
 * @return {string} platform - The formatted platform to match our enum format
 */
const formatPlatform = ({ platform }) => (`PLATFORM_${platform.toUpperCase()}`);

/**
 * @typedef {Object} page
 * @property {string} name
 */

/**
 * Format Activity event's page data.
 *
 * @function formatPage
 * @param {string} options.pageName - The name of the page
 * @return {page}
 */
const formatPage = ({ pageName }) => ({
    name: pageName
});

const formatClientError = (error) => ({
    message: error.message,
    description: ERRORS.REPORT_ACTIVITY_CLIENT_FAILED,
    status: 0
});

const formatServerError = async(response) => ({
    message: await formatResponseErrorMessage(response),
    description: ERRORS.REPORT_ACTIVITY_SERVER_FAILED,
    status: response.status
});

const formatResponseErrorMessage = async(response) => {
    const errorText = await response.text();

    try {
        return JSON.parse(errorText).message;
    } catch (error) {
        return errorText;
    }
};

const formatHeaders = () => {
    const headers = {
        url: window.location.href
    };

    if (document.referrer) {
        headers.referrer = document.referrer;
    }

    return headers;
};

/**
 * Creates a new URL by combining the specified URLs
 *
 * @param {string} baseURL The base URL
 * @param {string} relativeURL The relative URL
 * @returns {string} The combined URL
 */
const combineURLs = (baseURL, relativeURL) => relativeURL
    ? `${baseURL.replace(/\/+$/, '')}/${relativeURL.replace(/^\/+/, '')}`
    : baseURL;
